import { useState, useEffect, useCallback, useRef } from "react";

import { Room, LocalParticipant, RemoteParticipant, LocalAudioTrack } from "twilio-video";

import { UserRole } from "@/types";

import { useDeepgram } from "./useDeepgram";

interface TranscriptEntry {
	text: string;
	speaker: 'Tutor' | 'Student';
	timestamp: Date;
}

export default function useFullTranscript(room: Room | null, userRole: UserRole, localTracks: LocalAudioTrack[] = []) {
	const [fullTranscript, setFullTranscript] = useState<TranscriptEntry[]>([]);
	const { startTranscription, stopTranscription } = useDeepgram();
	const activeTranscriptionsRef = useRef<Set<string>>(new Set());

	const getSpeakerRole = useCallback((participantId: string): 'Tutor' | 'Student' => {
		if (participantId === 'local') {
			return userRole === UserRole.Tutor ? 'Tutor' : 'Student';
		} else {
			return userRole === UserRole.Tutor ? 'Student' : 'Tutor';
		}
	}, [userRole]);

	const startTranscriptionForParticipant = useCallback((participant: LocalParticipant | RemoteParticipant, participantId: string) => {
		if (!room || userRole !== UserRole.Tutor) return;

		if (activeTranscriptionsRef.current.has(participantId)) {
			return;
		}

		let audioTrack;
		
		// For local participant, use localTracks
		if (participantId === 'local') {
			audioTrack = localTracks.find(track => track.kind === 'audio');
		} else {
			// For remote participants, use participant.audioTracks
			audioTrack = (Array.from(participant.audioTracks.values() as any)[0] as any)?.track;
		}

		if (!audioTrack) {
			return;
		}

		activeTranscriptionsRef.current.add(participantId);

		const speaker = getSpeakerRole(participantId);

		startTranscription(participantId, audioTrack, speaker, (transcriptData) => {
			const entry: TranscriptEntry = {
				text: transcriptData.text,
				speaker: transcriptData.speaker,
				timestamp: new Date(),
			};

			setFullTranscript((prev) => {
				const lastEntry = prev[prev.length - 1];
				if (lastEntry && lastEntry.text === entry.text && lastEntry.speaker === entry.speaker) {
					return prev;
				}
				return [...prev, entry];
			});
		});
	}, [room, userRole, startTranscription, localTracks, getSpeakerRole]);

	const startTranscriptionForAllParticipants = useCallback(() => {
		if (!room || userRole !== UserRole.Tutor) return;

		// Start transcription for local participant
		const localParticipant = room.localParticipant;
		if (localParticipant) {
			startTranscriptionForParticipant(localParticipant, 'local');
		}

		// Start transcription for all remote participants
		Array.from(room.participants.values()).forEach((participant) => {
			startTranscriptionForParticipant(participant, participant.identity);
		});
	}, [room, userRole, startTranscriptionForParticipant]);

	// Restart local transcription when localTracks change
	useEffect(() => {
		if (room && userRole === UserRole.Tutor) {
			const localParticipant = room.localParticipant;
			if (localParticipant) {
				startTranscriptionForParticipant(localParticipant, 'local');
			}
		}
	}, [localTracks, startTranscriptionForParticipant, room, userRole]);

	useEffect(() => {
		if (!room || userRole !== UserRole.Tutor) return;

		const handleParticipantConnected = (participant: RemoteParticipant) => {
			startTranscriptionForParticipant(participant, participant.identity);
		};

		const handleParticipantDisconnected = (participant: RemoteParticipant) => {
			// Clean up transcription for disconnected participant
			activeTranscriptionsRef.current.delete(participant.identity);
			stopTranscription(participant.identity);
		};

		const handleTrackSubscribed = (track: any, _publication: any, participant: RemoteParticipant) => {
			if (track.kind === 'audio') {
				startTranscriptionForParticipant(participant, participant.identity);
			}
		};

		const handleTrackUnsubscribed = (track: any, _publication: any, participant: RemoteParticipant) => {
			if (track.kind === 'audio') {
				// Stop transcription for this participant's audio track
				activeTranscriptionsRef.current.delete(participant.identity);
				stopTranscription(participant.identity);
			}
		};

		// Handle local track publications
		const handleTrackPublished = (publication: any) => {
			if (publication.track?.kind === 'audio') {
				startTranscriptionForParticipant(room.localParticipant, 'local');
			}
		};

		const handleTrackUnpublished = (publication: any) => {
			if (publication.track?.kind === 'audio') {
				activeTranscriptionsRef.current.delete('local');
				stopTranscription('local');
			}
		};

		room.on("participantConnected", handleParticipantConnected);
		room.on("participantDisconnected", handleParticipantDisconnected);
		room.on("trackSubscribed", handleTrackSubscribed);
		room.on("trackUnsubscribed", handleTrackUnsubscribed);
		room.on("trackPublished", handleTrackPublished);
		room.on("trackUnpublished", handleTrackUnpublished);

		// Start transcription for existing participants
		startTranscriptionForAllParticipants();

		return () => {
			room.off("participantConnected", handleParticipantConnected);
			room.off("participantDisconnected", handleParticipantDisconnected);
			room.off("trackSubscribed", handleTrackSubscribed);
			room.off("trackUnsubscribed", handleTrackUnsubscribed);
			room.off("trackPublished", handleTrackPublished);
			room.off("trackUnpublished", handleTrackUnpublished);
		};
	}, [room, userRole, startTranscriptionForAllParticipants, startTranscriptionForParticipant, stopTranscription]);

	// Clean up on unmount
	useEffect(() => {
		return () => {
			activeTranscriptionsRef.current.clear();
			stopTranscription();
		};
	}, [stopTranscription]);

	return {
		fullTranscript,
	};
} 